(() => {
  if (customElements.get('app-inspired-navigation')) return;

  class AppInspiredNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.pending = new Set();
      this.opened = false;
      this.handoff = false;
      const options = { signal: this.controller.signal };
      document.addEventListener('drawerOpen.NavDrawer', () => this.syncState(), options);
      document.addEventListener('keydown', (event) => this.onKeydown(event), { ...options, capture: true });
      document.addEventListener('focusin', (event) => this.onFocusin(event), { ...options, capture: true });
      document.addEventListener('click', (event) => this.onClick(event), { ...options, capture: true });
      document.addEventListener('shopify:section:unload', (event) => {
        const sectionId = event.detail?.sectionId;
        if (sectionId ? sectionId === this.dataset.headerSectionId : event.target?.contains(this)) this.disconnectedCallback();
      }, options);
      window.addEventListener('resize', () => this.position(), options);
      window.visualViewport?.addEventListener('resize', () => this.position(), options);
      this.mutationObserver = new MutationObserver(() => this.syncState());
      this.mutationObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
      this.header = document.getElementById('SiteHeader');
      if (this.header && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.position());
        this.resizeObserver.observe(this.header);
      }
      this.position();
      this.syncState();
    }

    nativeDrawer() {
      const drawer = window.theme?.NavDrawer;
      return drawer?.drawer === this ? drawer : null;
    }

    schedule(callback, delay) {
      const id = window.setTimeout(() => {
        this.pending.delete(id);
        if (this.controller && !this.controller.signal.aborted) callback();
      }, delay);
      this.pending.add(id);
      return id;
    }

    cancel(id) {
      window.clearTimeout(id);
      this.pending.delete(id);
    }

    position() {
      const bottom = this.header?.getBoundingClientRect().bottom || 0;
      this.style.setProperty('--anav-top', `${Math.max(0, Math.min(window.innerHeight, bottom))}px`);
    }

    syncState() {
      const open = this.classList.contains('drawer--is-open');
      this.setAttribute('aria-hidden', String(!open));
      if (open && !this.opened) {
        this.handoff = false;
        this.cancel(this.cartTimer);
        const surface = this.querySelector('.anav__surface');
        if (surface) {
          surface.scrollTop = 0;
          surface.scrollLeft = 0;
        }
        this.position();
        this.focusTimer = this.schedule(() => {
          if (this.active()) this.focusFirst();
        }, 0);
      } else if (!open) {
        this.cancel(this.focusTimer);
      }
      this.opened = open;
    }

    active() {
      return this.opened && !this.handoff && this.classList.contains('drawer--is-open');
    }

    focusable() {
      return Array.from(this.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]')).filter((element) => (
        element.tabIndex >= 0 && !element.disabled && element.getClientRects().length > 0 &&
        !element.closest('[inert], [hidden], [aria-hidden="true"]')
      ));
    }

    focusFirst() {
      const first = this.querySelector('.js-drawer-close');
      const focusable = this.focusable();
      (focusable.includes(first) ? first : focusable[0] || this).focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (!this.active() || event.key !== 'Tab' || event.defaultPrevented) return;
      const focusable = this.focusable();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const outside = !this.contains(document.activeElement);
      if (!first || outside || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last || this : first || this).focus({ preventScroll: true });
      }
    }

    onFocusin(event) {
      if (this.active() && !this.contains(event.target)) this.focusFirst();
    }

    releaseSource() {
      this.handoff = true;
      this.cancel(this.focusTimer);
      const drawer = this.nativeDrawer();
      if (!drawer) return;
      drawer.activeSource?.setAttribute('aria-expanded', 'false');
      drawer.activeSource = null;
    }

    onClick(event) {
      const element = event.target?.nodeType === 3 ? event.target.parentElement : event.target;
      const trigger = element?.closest?.('[data-rewards-toggle], [data-kabarai-header-toggle], a[href]');
      if (!trigger || !this.contains(trigger)) return;
      const href = trigger.getAttribute('href') || '';
      if (trigger.hasAttribute('data-rewards-toggle') || trigger.hasAttribute('data-kabarai-header-toggle') || href.includes('rewards-circle')) {
        this.releaseSource();
        return;
      }
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || trigger.hasAttribute('download')) return;
      if (trigger.getAttribute('target') && trigger.getAttribute('target') !== '_self') return;
      if (!href.trim() || href.trim() === '#') return;
      const drawer = this.nativeDrawer();
      if (!drawer?.isOpen) return;
      const cartTrigger = Array.from(document.querySelectorAll('#SiteHeader .js-drawer-open-cart')).find((button) => !this.contains(button));
      const useCartDrawer = trigger.hasAttribute('data-app-nav-cart') && window.theme?.settings?.cartType === 'drawer' &&
        !document.body.classList.contains('template-cart') && document.getElementById('CartDrawer') && cartTrigger;
      this.releaseSource();
      drawer.close();
      this.syncState();
      if (!useCartDrawer) return;
      event.preventDefault();
      this.cancel(this.cartTimer);
      this.cartTimer = this.schedule(() => {
        if (cartTrigger.isConnected && document.getElementById('CartDrawer')) cartTrigger.click();
        else window.location.assign(trigger.href);
      }, 500);
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.releaseSource();
      const drawer = this.nativeDrawer();
      if (drawer?.isOpen) drawer.close();
      this.controller.abort();
      this.controller = null;
      this.mutationObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.pending.forEach((id) => window.clearTimeout(id));
      this.pending.clear();
      this.setAttribute('aria-hidden', 'true');
      this.style.removeProperty('--anav-top');
      this.opened = false;
    }
  }

  customElements.define('app-inspired-navigation', AppInspiredNavigation);
})();
